import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import { displayLocation } from '../location';
import { fmtWh } from '../time';
import FloorPlan from './FloorPlan';
import StorageTiersTab from './StorageTiersTab';

// Settings → Warehouse (Fernando, 2026-09-29): ONE plan — the floor plan — and four tabs:
//   Tools          the one-off tools: Calibrate (walks), Mark stacks, Units, Rebuild from the measurements
//   Aisles         the aisle records (wh_aisles): walk order, box label, faces; one page per aisle
//                  showing the connection between its two faces as the floor plan has it
//   Smart Picking  carts, multi-box threshold, packing end (warehouse_layout.picking / packing_end)
//   Floor Plan     the floor plan itself (was Warehouse → Floor plan in the sidebar)
// The old aisle editor (typed code lists, CSV) is gone: the stops of an aisle are written by
// the calibration walk and the "rebuild from the measurements" tool only. Claude, 2026-09-29.

export const WAREHOUSE_TABS = [
  { key: 'tools', label: 'Tools' },
  { key: 'aisles', label: 'Aisles' },
  { key: 'smart-picking', label: 'Smart Picking' },
  { key: 'floor', label: 'Floor Plan' },
  { key: 'storage', label: 'Storage tiers' },     // storage tier per NOS style (docs/STORAGE-TIER.md, 2026-10-31)
] as const;
export type WarehouseSub = typeof WAREHOUSE_TABS[number]['key'];

const role = () => localStorage.getItem('wms_role') || 'staff';
const canEdit = () => role() !== 'viewer';
const canApply = () => role() === 'admin' || role() === 'manager';

async function jget(url: string) {
  const r = await authFetch(url);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
}
async function jsend(url: string, method: string, body: any) {
  const r = await authFetch(url, { method, body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
}

export default function WarehouseSettings({ sub, aisleId }: { sub: WarehouseSub; aisleId?: string }) {
  const navigate = useNavigate();
  return (
    <div>
      <div className="flex gap-1 border-b border-gray-800 mb-5 overflow-x-auto">
        {WAREHOUSE_TABS.map(t => (
          <button key={t.key} onClick={() => navigate(`/settings/warehouse/${t.key}`)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${
              sub === t.key ? 'text-white border-b-2 border-blue-500' : 'text-gray-400 hover:text-white'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === 'tools' && <ToolsTab />}
      {sub === 'aisles' && (aisleId ? <AislePage name={aisleId} /> : <AislesTab />)}
      {sub === 'smart-picking' && <SmartPickingTab />}
      {sub === 'storage' && <StorageTiersTab />}
      {sub === 'floor' && (
        <div className="rounded-xl border border-gray-800 overflow-hidden" style={{ height: 'calc(100vh - 210px)', minHeight: 480 }}>
          <FloorPlan />
        </div>
      )}
    </div>
  );
}

// ─── Tools ───────────────────────────────────────────────────────────────────

function ToolsTab() {
  const navigate = useNavigate();
  const tools: { title: string; to: string; when: string; roles?: 'apply' }[] = [
    { title: 'Calibrate', to: '/warehouse/calibrate',
      when: 'Walk a face unit by unit (what stands there: bay system, shelves, pallet setup) or check the codes of an aisle stop by stop. Applying a walk rewrites that aisle\'s picking stops.' },
    { title: 'Rebuild picking stops from the measurements', to: '/warehouse/calibrate/layout', roles: 'apply',
      when: 'Pairs the two faces of an aisle by their measured positions and replaces the aisle\'s stops. Use it after a renumbering or when an aisle page says the stops differ from the floor plan.' },
    { title: 'Mark stacks', to: '/warehouse/stacks',
      when: 'Pallet by pallet from the bottom wall up: which box type (S, M, SL, LL, ML) stands on each section. Colours the floor plan.' },
    { title: 'Units', to: '/warehouse/units',
      when: 'Every pallet and metal bay the walks found, its sections and stock, the observed capacity per setup, and the numbering check per face.' },
    { title: 'Å shelves — returned pieces', to: '/warehouse/pieces',
      when: 'The returned / cancelled Ä pieces lying on Å 1–3 against Inbound station 1, one row per piece. Managers put pieces there by hand and take them out; picking takes them as the last stop of the walk.' },
  ];
  return (
    <div>
      <p className="text-gray-500 text-sm mb-4">One-off tools that write into the warehouse model. Everyday reading is on the Aisles and Floor Plan tabs.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {tools.filter(t => t.roles !== 'apply' || canApply()).map(t => (
          <button key={t.to} onClick={() => navigate(t.to)}
            className="text-left rounded-xl border border-gray-800 bg-[#1a1d27] p-4 hover:border-blue-500 transition-colors">
            <div className="text-white font-semibold text-sm mb-1">{t.title} →</div>
            <div className="text-gray-400 text-xs leading-relaxed">{t.when}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Aisles ──────────────────────────────────────────────────────────────────

interface AisleRow {
  id: string; name: string; walk_order: number | null; box_label: string; active: boolean;
  width_top_cm: number | null; width_bottom_cm: number | null;
  stops: number; sections: number; stocked_codes: number; pieces: number;
  left: string | null; right: string | null; kind: { left: string | null; right: string | null };
  calibrated_sections: number; calibrated_at: string | null;
  last_walk: { id: number; status: string; actor: string | null; started_at: string; applied_at: string | null } | null;
}

function AislesTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AisleRow[] | null>(null);
  const [packingEnd, setPackingEnd] = useState<'near' | 'far'>('near');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => jget(`${API}/warehouse/aisles`).then(d => { setRows(d.aisles); setPackingEnd(d.packing_end || 'near'); }).catch(e => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  if (err && !rows) return <div className="text-red-400 text-sm">{err}</div>;
  if (!rows) return <div className="text-gray-500 text-sm">Loading…</div>;
  const active = rows.filter(r => r.active);
  const move = async (name: string, dir: -1 | 1) => {
    const names = active.map(r => r.name);
    const i = names.indexOf(name), j = i + dir;
    if (i < 0 || j < 0 || j >= names.length) return;
    [names[i], names[j]] = [names[j], names[i]];
    setBusy(true); setErr('');
    try { await jsend(`${API}/warehouse/aisles/order`, 'POST', { names }); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const save = async (name: string, body: any) => {
    setBusy(true); setErr('');
    try { await jsend(`${API}/warehouse/aisles/${encodeURIComponent(name)}`, 'PUT', body); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const edit = canEdit();
  return (
    <div>
      <p className="text-gray-500 text-sm mb-3">
        The aisles in the order the picking plan visits them (the packing station is at the {packingEnd === 'near' ? 'stop-1' : 'last-stop'} end —
        Smart Picking). The stops of an aisle come from the calibration walk and the measurements; open an aisle to see them as the floor plan has them.
        The box label is what the cart box shows.
      </p>
      {err && <div className="text-red-400 text-sm mb-2">{err}</div>}
      <div className="overflow-x-auto rounded-xl border border-gray-800">
        <table className="w-full text-sm">
          <thead className="text-left text-gray-500 bg-[#1a1d27]">
            <tr>
              <th className="px-3 py-2 w-16">Order</th><th className="px-3 py-2">Aisle</th><th className="px-3 py-2">Faces</th>
              <th className="px-3 py-2">Box label</th><th className="px-3 py-2 text-right">Stops</th><th className="px-3 py-2 text-right">Codes</th>
              <th className="px-3 py-2 text-right">Stocked</th><th className="px-3 py-2 text-right">Pieces</th><th className="px-3 py-2">Width</th>
              <th className="px-3 py-2">Last walk</th><th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.name} className={`border-t border-gray-800 ${r.active ? 'text-gray-200' : 'text-gray-600'}`}>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  {r.active ? <>
                    <span className="inline-block w-5 text-gray-400">{i + 1}</span>
                    {edit && <>
                      <button disabled={busy || i === 0} onClick={() => move(r.name, -1)} className="text-gray-500 hover:text-white disabled:opacity-20 px-0.5">▲</button>
                      <button disabled={busy || i === active.length - 1} onClick={() => move(r.name, 1)} className="text-gray-500 hover:text-white disabled:opacity-20 px-0.5">▼</button>
                    </>}
                  </> : <span className="text-xs">off</span>}
                </td>
                <td className="px-3 py-1.5">
                  <button onClick={() => navigate(`/settings/warehouse/aisles/${encodeURIComponent(r.name)}`)} className="font-semibold text-white hover:text-blue-300">{r.name}</button>
                </td>
                <td className="px-3 py-1.5 text-xs whitespace-nowrap">
                  {r.left ? <span title={r.kind.left || ''}>{r.left} <span className="text-gray-500">{r.kind.left === 'metal' ? 'bays' : 'pallets'}</span></span> : '—'}
                  <span className="text-gray-600"> | </span>
                  {r.right ? <span title={r.kind.right || ''}>{r.right} <span className="text-gray-500">{r.kind.right === 'metal' ? 'bays' : 'pallets'}</span></span> : '—'}
                </td>
                <td className="px-3 py-1.5">
                  {edit ? (
                    <input key={r.box_label} defaultValue={r.box_label} disabled={busy}
                      onBlur={e => { const v = e.target.value.trim(); if (v && v !== r.box_label) save(r.name, { box_label: v }); }}
                      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                      className="w-24 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white text-sm" />
                  ) : r.box_label}
                </td>
                <td className="px-3 py-1.5 text-right">{r.stops}</td>
                <td className="px-3 py-1.5 text-right">{r.sections}</td>
                <td className="px-3 py-1.5 text-right">{r.stocked_codes}</td>
                <td className="px-3 py-1.5 text-right">{r.pieces.toLocaleString('en-US').replace(/,/g, ' ')}</td>
                <td className="px-3 py-1.5 text-xs whitespace-nowrap">{r.width_top_cm != null ? `${r.width_top_cm}–${r.width_bottom_cm} cm` : '—'}</td>
                <td className="px-3 py-1.5 text-xs whitespace-nowrap">
                  {r.last_walk ? <span className={r.last_walk.status === 'open' ? 'text-amber-300' : ''}>{r.last_walk.status} · {r.last_walk.actor || '—'} · {fmtWh(r.last_walk.applied_at || r.last_walk.started_at, 'date')}</span>
                    : r.calibrated_at ? `calibrated ${fmtWh(r.calibrated_at, 'date')}` : <span className="text-gray-600">never</span>}
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  {edit && <button disabled={busy} onClick={() => save(r.name, { active: !r.active })}
                    className="text-xs text-gray-500 hover:text-white">{r.active ? 'switch off' : 'switch on'}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-gray-600 text-xs mt-2">An aisle switched off leaves the picking plan (its codes become "no location"); its stops and walks are kept.</p>
    </div>
  );
}

// ─── one aisle ───────────────────────────────────────────────────────────────

interface Sec { code: string; top: number | null; len: number | null; unit_id: string | null; unit_setup: string | null; unit_kind: string | null;
  reserved: boolean; floor_space: boolean; label: string | null; stop_index: number | null; pieces: number }
interface Face { letter: string; row: string; kind: 'pallet' | 'metal'; numbering: string; system: string | null; pitch_cm: number;
  sections: Sec[]; stocked: number; not_in_stops: string[] }
interface Stop { index: number; left: string[]; right: string[]; y: number | null; pieces: number }
interface Page {
  aisle: { name: string; layout_id: string | null; walk_order: number | null; box_label: string; active: boolean;
           width_top_cm: number | null; width_bottom_cm: number | null; updated_at: string | null; updated_by: string | null };
  aisles_total: number; packing_end: 'near' | 'far';
  faces: { left: Face | null; right: Face | null };
  stops: Stop[];
  measured: { stops: number; pairs_kept: number; pairs_today: number; lost_with_stock: { code: string; pieces: number }[]; codes: number } | null;
  walks: { id: number; mode: string; face: string | null; actor: string | null; status: string; started_at: string; applied_at: string | null; applied_by: string | null }[];
  distance: { station: string | null; to_first_stop: number | null; to_last_stop: number | null; along: number | null; first: string; last: string } | null;
}

const SCALE = 0.22;           // px per cm on the aisle strip

function AislePage({ name }: { name: string }) {
  const navigate = useNavigate();
  const [p, setP] = useState<Page | null>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => jget(`${API}/warehouse/aisles/${encodeURIComponent(name)}/page`).then(setP).catch(e => setErr(e.message)), [name]);
  useEffect(() => { load(); }, [load]);

  const strip = useMemo(() => {
    if (!p) return null;
    const secs = [...(p.faces.left?.sections || []), ...(p.faces.right?.sections || [])].filter(s => s.top != null);
    if (!secs.length) return null;
    const y0 = Math.min(...secs.map(s => s.top as number)) - 60;
    const y1 = Math.max(...secs.map(s => (s.top as number) + (s.len || 40))) + 60;
    const pos: Record<string, Sec> = {};
    secs.forEach(s => { pos[s.code] = s; });
    return { y0, y1, pos, h: (y1 - y0) * SCALE };
  }, [p]);

  if (err && !p) return <div><button onClick={() => navigate('/settings/warehouse/aisles')} className="text-gray-400 hover:text-white text-sm">← Aisles</button><div className="text-red-400 text-sm mt-2">{err}</div></div>;
  if (!p) return <div className="text-gray-500 text-sm">Loading…</div>;
  const a = p.aisle;
  const L = p.faces.left, R = p.faces.right;
  const W = 380, faceW = 110, gap = W - 2 * faceW;
  const yOf = (cm: number) => (cm - (strip ? strip.y0 : 0)) * SCALE;
  const differs = p.measured && p.measured.pairs_today > 0 && p.measured.pairs_kept < p.measured.pairs_today;
  const rebuild = async () => {
    if (!p.measured) return;
    if (!window.confirm(`Replace the ${p.stops.length} stops of ${a.name} with the ${p.measured.stops} stops the measurements give? The previous layout is kept in the history.`)) return;
    setBusy(true); setMsg('');
    try { await jsend(`${API}/warehouse/layout/from-sections/apply`, 'POST', { aisles: [a.name] }); setMsg('Stops rebuilt from the measurements.'); await load(); }
    catch (e: any) { setMsg(e.message); } finally { setBusy(false); }
  };
  const secRect = (s: Sec, side: 'left' | 'right') => {
    const x = side === 'left' ? W - faceW : 0;
    const y = yOf(s.top as number), h = Math.max(6, (s.len || 40) * SCALE - 1);
    const inStop = p.stops.some(st => st.left.includes(s.code) || st.right.includes(s.code));
    const fill = s.reserved ? 'none' : s.pieces > 0 ? (s.unit_kind === 'bay' || L?.kind === 'metal' && side === 'left' || R?.kind === 'metal' && side === 'right' ? '#334155' : '#1f6f63') : '#1f2937';
    return (
      <g key={s.code}>
        <rect x={x} y={y} width={faceW} height={h} fill={fill} stroke={!inStop && s.pieces > 0 ? '#f59e0b' : s.reserved ? '#6b7280' : '#0f1117'}
          strokeDasharray={s.reserved || s.floor_space ? '4 3' : undefined} strokeWidth={!inStop && s.pieces > 0 ? 2 : 1} />
        {h >= 8 && <text x={x + 4} y={y + h / 2 + 3.5} fontSize={Math.min(10, h - 1)} fill={s.pieces > 0 ? '#e5e7eb' : '#6b7280'}>
          {s.label ? `${s.label} · ` : ''}{displayLocation(s.code)}{s.pieces ? ` · ${s.pieces}` : ''}</text>}
        <title>{`${displayLocation(s.code)} — ${s.pieces} pcs${s.unit_id ? ` · ${s.unit_id}${s.unit_setup ? ` (${s.unit_setup})` : ''}` : ''}${s.reserved ? ' · reserved hole' : ''}${s.floor_space ? ' · floor space' : ''}${!inStop ? ' · IN NO STOP' : ''}`}</title>
      </g>
    );
  };
  return (
    <div>
      <button onClick={() => navigate('/settings/warehouse/aisles')} className="text-gray-400 hover:text-white text-sm">← Aisles</button>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mt-1 mb-3">
        <h2 className="text-2xl font-bold text-white">{a.name}</h2>
        <span className="text-gray-400 text-sm">walked {a.walk_order != null ? `${a.walk_order + 1} of ${p.aisles_total}` : '—'} · box <b className="text-gray-200">{a.box_label}</b>
          {a.width_top_cm != null && <> · {a.width_top_cm}–{a.width_bottom_cm} cm wide</>}{!a.active && <span className="text-amber-300"> · switched off</span>}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-5">
        <div>
          <div className="text-xs text-gray-500 mb-1 flex justify-between px-1">
            <span>{R ? `${R.letter} · ${R.kind === 'metal' ? R.system : 'pallets'} · ${R.numbering === 'up' ? '1 → n' : 'n → 1'}` : 'no right face'}</span>
            <span>top wall ↑</span>
            <span>{L ? `${L.letter} · ${L.kind === 'metal' ? L.system : 'pallets'} · ${L.numbering === 'up' ? '1 → n' : 'n → 1'}` : 'no left face'}</span>
          </div>
          <div className="rounded-xl border border-gray-800 bg-[#0f1117] overflow-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
            {!strip && <div className="text-gray-500 text-sm p-4">No measured section positions on this aisle yet — the strip is drawn from them.</div>}
            {strip && <svg width={W} height={strip.h} className="block mx-auto">
              {/* the stops: a band across the aisle joining the paired sections */}
              {p.stops.map(st => {
                const ys = [...st.left, ...st.right].map(c => strip!.pos[c]).filter(Boolean).map(s => [yOf(s.top as number), yOf((s.top as number) + (s.len || 40))]);
                if (!ys.length) return null;
                const y = Math.min(...ys.map(v => v[0])), h = Math.max(...ys.map(v => v[1])) - y;
                const lone = !st.left.length || !st.right.length;
                return (
                  <g key={st.index}>
                    <rect x={faceW} y={y} width={gap} height={h} fill={st.index % 2 ? '#1d4ed8' : '#2563eb'} opacity={lone ? 0.12 : 0.28} />
                    {h >= 9 && <text x={W / 2} y={y + h / 2 + 3.5} textAnchor="middle" fontSize={10} fill="#93c5fd">{st.index + 1}</text>}
                    <title>{`stop ${st.index + 1}: ${st.left.map(displayLocation).join(' ') || '—'} | ${st.right.map(displayLocation).join(' ') || '—'} · ${st.pieces} pcs`}</title>
                  </g>
                );
              })}
              {R?.sections.filter(s => s.top != null).map(s => secRect(s, 'right'))}
              {L?.sections.filter(s => s.top != null).map(s => secRect(s, 'left'))}
              {[0, 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500].filter(v => v > strip.y0 && v < strip.y1).map(v => (
                <text key={v} x={W / 2} y={yOf(v) - 2} textAnchor="middle" fontSize={9} fill="#4b5563">{v / 100} m</text>
              ))}
            </svg>}
          </div>
          <div className="text-[11px] text-gray-500 mt-1 space-x-3">
            <span><span className="inline-block w-3 h-3 align-middle mr-1 bg-[#2563eb]/40" />stop (numbered)</span>
            <span><span className="inline-block w-3 h-3 align-middle mr-1 border-2 border-amber-500" />stocked, in no stop</span>
            <span>dashed = reserved / floor space</span>
          </div>
        </div>

        <div className="space-y-4 text-sm">
          <div className="rounded-xl border border-gray-800 bg-[#1a1d27] p-4">
            <div className="text-white font-semibold mb-1">Picking stops</div>
            <div className="text-gray-400">{p.stops.length} stops · {p.stops.reduce((n, s) => n + s.left.length + s.right.length, 0)} codes · {p.stops.reduce((n, s) => n + s.pieces, 0).toLocaleString('en-US').replace(/,/g, ' ')} pcs</div>
            {p.measured && (
              <div className={`mt-2 text-xs ${differs ? 'text-amber-300' : 'text-emerald-300'}`}>
                {differs
                  ? `The measurements would give ${p.measured.stops} stops; ${p.measured.pairs_kept} of today's ${p.measured.pairs_today} left|right pairs match them.`
                  : `The stops match the measured positions (${p.measured.stops} stops).`}
                {p.measured.lost_with_stock.length > 0 && <div className="text-red-300 mt-1">Would lose their stop: {p.measured.lost_with_stock.map(x => `${displayLocation(x.code)} (${x.pieces} pcs)`).join(', ')} — place them first.</div>}
              </div>
            )}
            {(L?.not_in_stops.length || R?.not_in_stops.length) ? (
              <div className="text-xs text-amber-300 mt-2">Stocked, in no stop (the picker never gets there): {[...(L?.not_in_stops || []), ...(R?.not_in_stops || [])].map(displayLocation).join(', ')}</div>
            ) : null}
            <div className="flex flex-wrap gap-2 mt-3">
              {canEdit() && a.layout_id && <button onClick={() => navigate(`/warehouse/calibrate/${encodeURIComponent(a.layout_id!)}`)}
                className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium">Walk this aisle (check codes)</button>}
              {canApply() && p.measured && <button disabled={busy || p.measured.lost_with_stock.length > 0} onClick={rebuild}
                className="px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white text-xs font-medium">Rebuild stops from the measurements</button>}
            </div>
            {msg && <div className="text-xs text-gray-300 mt-2">{msg}</div>}
          </div>

          <div className="rounded-xl border border-gray-800 bg-[#1a1d27] p-4">
            <div className="text-white font-semibold mb-1">On the floor plan</div>
            {p.distance ? (
              <div className="text-gray-300 text-xs space-y-0.5">
                <div>From <b>{p.distance.station || 'the P&P station'}</b> to stop 1 ({displayLocation(p.distance.first)}): <b>{p.distance.to_first_stop != null ? `${p.distance.to_first_stop} m` : 'no way through'}</b></div>
                <div>To the last stop ({displayLocation(p.distance.last)}): <b>{p.distance.to_last_stop != null ? `${p.distance.to_last_stop} m` : 'no way through'}</b></div>
                <div>Along the aisle, stop 1 → last: <b>{p.distance.along != null ? `${p.distance.along} m` : '—'}</b></div>
                <div className="text-gray-500 mt-1">Walked around every pallet, bay, the mezzanine and the stations, down the middle of the aisle — the same route the P&P statistics use.</div>
              </div>
            ) : <div className="text-gray-500 text-xs">No P&P station with an entrance is placed on the floor plan yet (Floor Plan → Plan moves → Stations).</div>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {(['right', 'left'] as const).map(side => {
              const f = p.faces[side];
              return (
                <div key={side} className="rounded-xl border border-gray-800 bg-[#1a1d27] p-4">
                  <div className="text-white font-semibold">{f ? `Face ${f.letter}` : `No ${side} face`} <span className="text-gray-500 font-normal text-xs">{side === 'left' ? 'picker\'s left' : 'picker\'s right'}</span></div>
                  {f && <div className="text-xs text-gray-400 mt-1">
                    row {f.row} · {f.kind === 'metal' ? `${f.system} bays` : 'pallets'} · {f.sections.length} sections, {f.stocked} stocked · pitch {f.pitch_cm} cm
                    <div className="mt-1">{f.sections.filter(s => s.unit_id).length} on a unit · {f.sections.filter(s => s.reserved).length} reserved · {f.sections.filter(s => s.floor_space).length} floor</div>
                    {canEdit() && <button onClick={() => navigate(`/warehouse/calibrate/face/${f.letter}`)} className="mt-2 px-3 py-1 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-xs">Units walk of {f.letter}</button>}
                  </div>}
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-gray-800 bg-[#1a1d27] p-4">
            <div className="text-white font-semibold mb-1">Walks</div>
            {p.walks.length ? (
              <div className="text-xs text-gray-400 space-y-0.5">
                {p.walks.map(w => (
                  <div key={w.id}>#{w.id} · {w.mode === 'units' ? `units of ${w.face}` : 'codes'} · {w.actor || '—'} · {fmtWh(w.started_at, 'datetime')} · <span className={w.status === 'open' ? 'text-amber-300' : w.status === 'applied' ? 'text-emerald-300' : ''}>{w.status}{w.applied_by ? ` by ${w.applied_by}` : ''}</span></div>
                ))}
              </div>
            ) : <div className="text-xs text-gray-500">No walk of this aisle yet.</div>}
            {a.updated_by && <div className="text-xs text-gray-600 mt-2">Aisle record last edited by {a.updated_by} · {fmtWh(a.updated_at, 'datetime')}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Smart Picking ───────────────────────────────────────────────────────────

interface Cart { id: string; name: string; boxes: number }
interface Picking { cart_size: number; threshold: number; carts: Cart[] }
const uid = () => Math.random().toString(36).slice(2, 10);

function SmartPickingTab() {
  const [packingEnd, setPackingEnd] = useState<'near' | 'far'>('near');
  const [picking, setPicking] = useState<Picking | null>(null);
  const [toast, setToast] = useState<'saving' | 'saved' | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    jget(`${API}/warehouse-layout`).then(cfg => {
      setPackingEnd(cfg.packing_end === 'far' ? 'far' : 'near');
      const carts: Cart[] = Array.isArray(cfg.picking?.carts) && cfg.picking.carts.length ? cfg.picking.carts : [{ id: 'cart1', name: 'Cart (4 boxes)', boxes: 4 }];
      setPicking({ cart_size: carts[0].boxes, threshold: Number(cfg.picking?.threshold) || 6, carts });
    }).catch(e => setErr(e.message));
  }, []);
  const persist = async (pe: 'near' | 'far', pk: Picking) => {
    setToast('saving'); setErr('');
    try { await jsend(`${API}/warehouse-layout`, 'PUT', { packing_end: pe, picking: pk }); setToast('saved'); }
    catch (e: any) { setErr(e.message); setToast(null); return; }
    setTimeout(() => setToast(null), 1500);
  };
  if (!picking) return <div className="text-gray-500 text-sm">{err || 'Loading…'}</div>;
  const edit = canEdit();
  const setCarts = (carts: Cart[]) => { const pk = { ...picking, carts, cart_size: carts[0]?.boxes ?? picking.cart_size }; setPicking(pk); persist(packingEnd, pk); };
  return (
    <div className="space-y-4 max-w-lg">
      {err && <div className="text-red-400 text-sm">{err}</div>}
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <div className="text-white text-sm font-medium">Packing station</div>
        <div className="text-gray-500 text-xs mt-0.5 mb-3">Which end of the aisles the packing station is at. The plan enters the first aisle from this end and alternates.</div>
        <div className="flex gap-2">
          {(['near', 'far'] as const).map(end => (
            <button key={end} disabled={!edit} onClick={() => { setPackingEnd(end); persist(end, picking); }}
              className={`px-4 py-2 text-sm rounded-lg border transition-colors ${packingEnd === end ? 'bg-green-700 border-green-600 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'}`}>
              {end === 'near' ? 'Near end (stop 1 side)' : 'Far end (last stop side)'}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <h3 className="text-white font-semibold text-sm mb-1">Carts</h3>
        <p className="text-gray-500 text-xs mb-4">The carts the warehouse owns and how many boxes each holds in one trip. Before a walk the tablet asks which cart is being taken (skipped when there is only one) and remembers the last answer on that tablet.</p>
        <div className="space-y-2">
          {picking.carts.map((c, i) => (
            <div key={c.id} className="flex items-center gap-3">
              <input value={c.name} placeholder={`Cart ${i + 1}`} disabled={!edit}
                onChange={e => setPicking({ ...picking, carts: picking.carts.map(x => x.id === c.id ? { ...x, name: e.target.value } : x) })}
                onBlur={() => persist(packingEnd, picking)}
                className="flex-1 bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
              <input type="number" min={1} max={99} value={c.boxes} disabled={!edit}
                onChange={e => { const carts = picking.carts.map(x => x.id === c.id ? { ...x, boxes: Math.max(1, parseInt(e.target.value) || 1) } : x); setPicking({ ...picking, carts, cart_size: carts[0]?.boxes ?? picking.cart_size }); }}
                onBlur={() => persist(packingEnd, picking)}
                className="w-20 bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
              <span className="text-gray-500 text-sm w-12">boxes</span>
              {edit && <button onClick={() => setCarts(picking.carts.filter(x => x.id !== c.id))} disabled={picking.carts.length <= 1}
                title={picking.carts.length <= 1 ? 'At least one cart is needed' : 'Remove this cart'}
                className="px-2 py-1 text-xs rounded-lg bg-gray-800 hover:bg-red-900/50 disabled:opacity-30 text-gray-400 hover:text-red-300">✕</button>}
            </div>
          ))}
        </div>
        {edit && <button onClick={() => setCarts([...picking.carts, { id: uid(), name: `Cart ${picking.carts.length + 1}`, boxes: 2 }])}
          className="mt-3 px-3 py-1.5 text-xs rounded-lg bg-gray-700 hover:bg-gray-600 text-white">+ Add cart</button>}
      </div>
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <h3 className="text-white font-semibold text-sm mb-1">Multi-box threshold</h3>
        <p className="text-gray-500 text-xs mb-4">When picking fewer orders than this number, the walk is one trip with every box (no boxes screen).</p>
        <div className="flex items-center gap-3">
          <input type="number" min={1} max={999} value={picking.threshold} disabled={!edit}
            onChange={e => setPicking({ ...picking, threshold: Math.max(1, parseInt(e.target.value) || 1) })}
            onBlur={() => persist(packingEnd, picking)}
            className="w-32 bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          <span className="text-gray-500 text-sm">orders — below this, one trip</span>
        </div>
      </div>
      <div className="bg-blue-900/20 border border-blue-800/40 rounded-xl p-4 text-xs text-blue-300">
        <strong>Example:</strong> Big cart 4 boxes, small cart 2, threshold 10.<br />
        • 3 orders → one trip with every box, no boxes screen<br />
        • 12 orders on the big cart → up to 4 boxes (aisles) per trip; on the small cart → 2 per trip, more trips
      </div>
      {toast && <div className={`fixed bottom-6 right-6 px-4 py-2 rounded-lg text-sm font-medium shadow-lg ${toast === 'saved' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'}`}>{toast === 'saved' ? 'Saved' : 'Saving…'}</div>}
    </div>
  );
}
